import PropTypes from 'prop-types';
import React from 'react';
import _ from 'lodash';
import classnames from 'classnames';

import FeatureFlagStore from '../../../stores/FeatureFlagStore';
import Search from '../../../records/Search';
import Smartcard from '../../../records/customer/Smartcard';
import { translate as t } from '../../../lib/I18n';
import { JourneyDirections, TimeTypes, TicketTypes } from '../../../constants/AppConstants';
import TrackingActions from "../../../actions/TrackingActions";

import Icon from '../../general/Icon';
import TimebandModal from './TimebandModal';
import OpenReturnCheckbox from '../OpenReturnCheckbox';
import DepartNowCheckbox from '../DepartNowCheckbox';

/**
 * DateTime summary that uses modals to capture dates and times,
 * used on small screens.
 */

class DateTimeSummaryCompact extends React.PureComponent {

    static propTypes = {
        search: PropTypes.instanceOf(Search),
        smartcard: PropTypes.instanceOf(Smartcard),
        onChange: PropTypes.func,
        disableTodayTomorrow: PropTypes.bool
    };

    static defaultProps = {
        search: new Search(),
        onChange: _.noop,
        disableTodayTomorrow: false
    };

    state = {
        modal: null
    };

    _closeModal = () => {
        this.setState({ modal: null });
    };

    _handleChange = (search) => {
        this.props.onChange(search);
        // This solves an IOS issue where the native select drawers
        // were popping up randomly after modal would close
        setTimeout(() => this._closeModal(), 1);
    };

    _hideDepartNow = () => {
        const { search, disableTodayTomorrow } = this.props;
        return search.smartcard && disableTodayTomorrow;
    };

    _getDateText (dt, labelText) {
        const m = dt.datetime;
        const arriveDepartText =
            dt.arriveDepart === TimeTypes.ARRIVE
                ? 'booking.dates.arriving'
                : 'booking.dates.departing';
        const arriveDepartTextShort = `${arriveDepartText}.short`;

        const day = m.calendar(null, {
            sameDay: '[Today]',
            nextDay: '[Tomorrow]',
            nextWeek: 'ddd DD MMM',
            sameElse: 'ddd DD MMM'
        });
        const time = m.format('HH:mm');

        return (
            <time className="clearfix" dateTime={ m.format() }>
                <div className="pull-left">
                    <div className="text-light datetime__fixed-width">{ labelText }</div>
                    <div>{ day }</div>
                </div>
                <div className="pull-right">
                    <div className="text-light" aria-hidden="true">{ t(arriveDepartTextShort) }</div>
                    <span className="sr-only">{ t(arriveDepartText) }</span>
                    <div>{ time }</div>
                </div>
            </time>
        );
    }

    _renderButton (dateTimeDirection, dateText) {
        const buttonProps = {
            className: 'btn-box',
            disabled: dateTimeDirection === JourneyDirections.OUTWARD && this.props.search.departNow,
            onClick: () => this.setState({ modal: dateTimeDirection })
        };

        return (
            <button { ...buttonProps }>
                <span className="datetime__selected-date pull-left">{ dateText }</span>
                <Icon className="pull-right box__icon" icon="calendar" />
            </button>
        );
    }

    renderRemoveReturn () {
        if (!this.props.search.isReturn()) {
            return null;
        }
        return (
            <button className="remove-return" onClick={ this.handleRemoveReturn } title="No Return">
                <Icon icon="close" />
            </button>
        );
    }

    _renderModal () {
        // This is required otherwise the DateTimeModal is
        // initialised with an old search record and retains
        // this in its state, meaning it resets the form back
        // to the default state with exception to the date picked
        if (!this.state.modal) {
            return null;
        }

        return (
            <TimebandModal
                show={ !!this.state.modal }
                search={ this.props.search }
                smartcard={ this.props.smartcard }
                type={ this.state.modal }
                onCancel={ this._closeModal }
                onSelect={ this._handleChange }
            />
        );
    }

    _renderOutboundDateTimeButton () {
        const datetime = this.props.search.get('outboundDateTime');
        const outboundText = this._getDateText(datetime, t('booking.dates.outbound'));
        return this._renderButton(JourneyDirections.OUTWARD, outboundText);
    }

    handleAddReturn = () => {
        TrackingActions.widgetSearchClick({ name: 'add-return' });
        this.setState({ modal: JourneyDirections.INWARD });
        this.props.onChange(this.props.search.setTicketType(TicketTypes.RETURN));
    };

    handleRemoveReturn = () => {
        TrackingActions.widgetSearchClick({ name: 'remove-return' });
        this.props.onChange(this.props.search.setTicketType(TicketTypes.SINGLE));
    };

    _renderReturnDateTimeButton () {
        if (this.props.search.isReturn()) {
            const datetime = this.props.search.get('returnDateTime');
            const outboundText = this._getDateText(datetime, t('booking.dates.return'));
            return this._renderButton(JourneyDirections.INWARD, outboundText, this.handleRemoveReturn);
        } else {
            return (
                <button
                    className='btn-box btn-add-return'
                    onClick={ this.handleAddReturn }>
                    <span className="pull-left text-light">{ t('booking.dates.addReturn') }</span>

                    <Icon className="pull-right box__icon" icon="add" />
                </button>
            );
        }
    }

    _getSummaryClasses () {
        return classnames('datetime__summary', 'margin-bottom--sm', {
            'datetime__summary--no-time': !FeatureFlagStore.get('enableTimeSelect')
        });
    }

    render () {
        const { search } = this.props;

        return (
            <div className="datetime__summary-compact">
                { this._renderModal() }

                <div className={ this._getSummaryClasses() }>
                    <div className="btn-box-group">
                        { this._renderOutboundDateTimeButton() }
                        { this._renderReturnDateTimeButton() }
                    </div>
                    { this.renderRemoveReturn() }
                </div>

                { !this._hideDepartNow() && <DepartNowCheckbox search={ search } onChange={ this._handleChange } /> }
                <OpenReturnCheckbox search={ search } onChange={ this._handleChange } />
            </div>
        );
    }
}

export default DateTimeSummaryCompact;



// WEBPACK FOOTER //
// ./src/components/search/dates/DateTimeSummaryCompact.js